const toNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const normalizeScore100 = (value, fallback = 0) => {
  let num = toNumber(value, fallback);
  if (num <= 10) num *= 10;
  if (num < 0) num = 0;
  if (num > 100) num = 100;
  return Math.round(num);
};

const ensureArray = (value) => (Array.isArray(value) ? value : []);
const ensureObject = (value) => (value && typeof value === 'object' && !Array.isArray(value) ? value : {});
const pickFirstArray = (...values) => {
  for (const value of values) {
    if (Array.isArray(value)) return value;
  }
  return [];
};
const pickFirstValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null) return value;
  }
  return null;
};
const pickFirstString = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

const extractInsights = (result, source) => {
  const cards = ensureArray(result?.cards);
  if (cards.length > 0) {
    return cards.map((card, idx) => ({
      id: card?.id || `insight_${idx + 1}`,
      title: card?.title || 'Insight',
      summary: card?.error || card?.analysis || card?.cause || card?.fix || '',
      timestamp: card?.timestamp || '00:00',
      level: card?.level || 'info',
      type: card?.type || 'analysis',
      training_id: card?.training_id || null
    }));
  }

  const summary = source?.main_error || source?.summary || result?.analysis_summary || '';
  if (summary) {
    return [{
      id: 'insight_main',
      title: 'Main issue',
      summary,
      timestamp: '00:00',
      level: 'info',
      type: 'analysis',
      training_id: null
    }];
  }

  return [];
};

const extractRecommendations = (result, insights) => {
  const recs = [];
  const cards = ensureArray(result?.cards);

  cards.forEach((card) => {
    const rec = (card?.suggestion || card?.fix || '').trim();
    if (rec) recs.push(rec);
  });

  const resultFallback = (result?.next_action || result?.analysis_summary || result?.message || '').trim();
  if (recs.length === 0 && resultFallback) recs.push(resultFallback);

  if (recs.length === 0 && insights.length > 0) {
    recs.push(insights[0]?.summary || 'Improve consistency.');
  }

  return [...new Set(recs)];
};

const extractResultScores = (result = {}) => {
  const scores = (result?.scores && typeof result.scores === 'object') ? result.scores : {};
  const normalized = {};
  Object.entries(scores).forEach(([key, value]) => {
    const num = Number(value);
    if (Number.isFinite(num)) normalized[key] = num;
  });
  return normalized;
};

export const normalizeAnalysis = (raw = {}, options = {}) => {
  const hasEmbeddedResult =
    !!raw?.result &&
    typeof raw.result === 'object' &&
    !Array.isArray(raw.result);
  const inferredLegacyResult = (!hasEmbeddedResult && typeof raw === 'object' && raw)
    ? raw
    : {};
  const result = hasEmbeddedResult ? raw.result : inferredLegacyResult;
  const source = raw || {};

  const score = normalizeScore100(source.score ?? source.overall_score ?? result.overall_score ?? 0);
  const overallScore = normalizeScore100(source.overall_score ?? result.overall_score ?? score);

  const insights = Array.isArray(source.insights) ? source.insights : extractInsights(result, source);
  const recommendations = Array.isArray(source.recommendations)
    ? source.recommendations
    : extractRecommendations(result, insights);
  const cards = ensureArray(result?.cards);
  const normalizedScores = extractResultScores(result);
  const strengths = pickFirstArray(source.strengths, result.strengths, source.pontos_fortes, result.pontos_fortes);
  const weaknesses = pickFirstArray(source.weaknesses, result.weaknesses, source.pontos_fracos, result.pontos_fracos);
  const corrections = pickFirstArray(source.corrections, result.corrections, source.correcoes, result.correcoes);
  const nextTraining = pickFirstValue(source.nextTraining, result.nextTraining, source.proximo_treino, result.proximo_treino);
  const detailedFeedback = pickFirstString(
    source.detailedFeedback,
    result.detailedFeedback,
    source.feedback_detalhado,
    result.feedback_detalhado
  );
  const trainingName = pickFirstString(source.trainingName, result.trainingName, source.nome_treino, result.nome_treino);
  const skills = ensureObject(pickFirstValue(source.skills, result.skills, normalizedScores));
  const primaryInsight = insights[0] || null;
  const headline = primaryInsight?.title
    || cards[0]?.title
    || 'Insight';
  const summary = primaryInsight?.summary
    || recommendations[0]
    || result?.analysis_summary
    || '';

  const normalized = {
    ...source,
    id: source.id || options.fallbackId || '',
    userId: source.userId || options.fallbackUserId || '',
    type: source.type || options.fallbackType || 'coach',
    game: source.game || result.game || options.fallbackGame || 'pubg',
    status: source.status || options.fallbackStatus || 'done',
    score,
    overall_score: overallScore,
    result,
    cards,
    scores: normalizedScores,
    skills,
    strengths,
    weaknesses,
    corrections,
    nextTraining,
    detailedFeedback,
    trainingName,
    insights,
    recommendations,
    headline,
    summary,
    screenshotUrl: source.screenshotUrl || cards.find(c => c.screenshotUrl)?.screenshotUrl || null,
    createdAt: source.createdAt || null,
    updatedAt: source.updatedAt || source.createdAt || null,
    sourcePipeline: source.sourcePipeline || options.fallbackPipeline || 'legacy',
    reservedCost: toNumber(source.reservedCost, 0),
    committedCost: toNumber(source.committedCost, 0),
    billingType: source.billingType || 'analysis_video',
    reservationStatus: source.reservationStatus || 'unknown'
  };

  if (normalized.type === 'sensi') {
    delete normalized.reservedCost;
    delete normalized.committedCost;
    delete normalized.billingType;
    delete normalized.reservationStatus;
  }

  return normalized;
};

export const normalizeAnalysisList = (items = []) => items.map((item) => normalizeAnalysis(item));

export const getAnalysisScore100 = (analysis) => normalizeAnalysis(analysis).overall_score;

export const getAnalysisScore10 = (analysis) => {
  const score100 = getAnalysisScore100(analysis);
  return (toNumber(score100, 0) / 10);
};

export const getAnalysisResult = (analysis) => normalizeAnalysis(analysis).result || {};

export const getAnalysisCards = (analysis) => normalizeAnalysis(analysis).cards || [];

export const getAnalysisScores = (analysis) => normalizeAnalysis(analysis).scores || {};

export const getAnalysisHeadline = (analysis) => normalizeAnalysis(analysis).headline || 'Insight';

export const getAnalysisSummary = (analysis) => normalizeAnalysis(analysis).summary || '';

export const getAnalysisRecommendations = (analysis) => normalizeAnalysis(analysis).recommendations || [];

export const getAnalysisCreatedAt = (analysis) => normalizeAnalysis(analysis).createdAt || null;
